// ─── Types ────────────────────────────────────────────────

export type SyncStatus = "synced" | "modified" | "new" | "workshop-only";

export interface ManifestEntry {
  workshop: string;
  production: string | null;
  status: SyncStatus;
  notes?: string;
}

export interface Manifest {
  lastSyncCommit: string;
  lastSyncDate: string;
  pages: ManifestEntry[];
  components: ManifestEntry[];
}

// ─── Minimal YAML parser ──────────────────────────────────

export function parseManifest(raw: string): Manifest {
  const manifest: Manifest = {
    lastSyncCommit: "",
    lastSyncDate: "",
    pages: [],
    components: [],
  };

  const commitMatch = raw.match(/^last_sync_commit:\s*(.+)$/m);
  if (commitMatch) {
    manifest.lastSyncCommit = commitMatch[1]!.trim();
  }

  const dateMatch = raw.match(/^last_sync_date:\s*"?(.+?)"?\s*$/m);
  if (dateMatch) {
    manifest.lastSyncDate = dateMatch[1]!.trim();
  }

  let currentSection: "pages" | "components" | null = null;
  let currentEntry: Partial<ManifestEntry> | null = null;

  for (const line of raw.split("\n")) {
    const trimmed = line.trim();

    if (trimmed === "pages:" || trimmed === "components:") {
      if (currentEntry?.workshop && currentSection) {
        manifest[currentSection].push(finalizeEntry(currentEntry));
        currentEntry = null;
      }
      currentSection = trimmed === "pages:" ? "pages" : "components";
      continue;
    }

    if (!currentSection) continue;
    if (trimmed === "" || trimmed.startsWith("#")) continue;

    if (trimmed.startsWith("- workshop:")) {
      if (currentEntry?.workshop) {
        manifest[currentSection].push(finalizeEntry(currentEntry));
      }
      currentEntry = {
        workshop: trimmed.replace("- workshop:", "").trim(),
      };
      continue;
    }

    if (!currentEntry) continue;

    if (trimmed.startsWith("production:")) {
      const val = trimmed.replace("production:", "").trim();
      currentEntry.production = val === "~" || val === "null" ? null : val;
    } else if (trimmed.startsWith("status:")) {
      currentEntry.status = trimmed
        .replace("status:", "")
        .trim() as SyncStatus;
    } else if (trimmed.startsWith("notes:")) {
      currentEntry.notes = trimmed
        .replace("notes:", "")
        .trim()
        .replace(/^"(.*)"$/, "$1");
    }
  }

  if (currentEntry?.workshop && currentSection) {
    manifest[currentSection].push(finalizeEntry(currentEntry));
  }

  return manifest;
}

function finalizeEntry(partial: Partial<ManifestEntry>): ManifestEntry {
  return {
    workshop: partial.workshop!,
    production: partial.production ?? null,
    status: partial.status ?? "new",
    notes: partial.notes,
  };
}
